/**
 * `<PilotBuildSheet>` — the PILOT page's sheet (`decisions/0206`): the combat sheet's pilot-on-foot
 * parts (HP, the eight stat cells with GRIT, the WEAPONS / ARMOR / GEAR rows) in their BUILD
 * posture, fed by {@link pilotBuildView}, plus the build sections the fight has no counterpart for
 * (HASE, skill triggers, talents, licenses, core bonuses, background, notes, the active mech).
 *
 * **Structurally read-only**, with the retired `BrutalistPilotLicense`'s three required-nullable
 * capabilities, so a host has to choose each:
 * - `onCommitNotes`: the owner's page mounts the NOTES textarea; everyone else reads the notes.
 * - `onRoll`: roll buttons on GRIT, HASE and the skill triggers, on the owner's page only; a press
 *   rolls the stat flat in place (`shell/railDice.ts`'s `RollEntry.roll`). A button on someone else's
 *   sheet would roll their stat as yours.
 * - `showCarried`: the owner's and the GM's page show the carried on-foot HP; another player's view
 *   shows the max (`SHARED_SHEET_DISCLOSURE.currentHp` is hidden).
 * - `edit`: an editing host's dispatch, undo scope and EDIT switch. Non-null mounts the EDIT button
 *   and, while EDIT is on, the controls of {@link usePilotSheetEdit} in place on the same sheet
 *   (`decisions/0206` point 4); `null` (SQUAD) has no EDIT button at all.
 */
import type { ReactNode } from "react";
import { resolveActiveMechId, type PilotData } from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import { listManufacturers } from "@lancer-vtt/compendium";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { useFictionOnly } from "../../lib/fictionOnly";
import { resolveMechArtUrl } from "../../lib/mechArt";
import { RulesText } from "../../lib/RulesText";
import { UserText } from "../../lib/UserText";
import { useDraftText } from "../../lib/useDraftText";
import { UnitSheet } from "../../lib/encounter/combatWindows/UnitSheet";
import { UnitLoadout } from "../../lib/encounter/combatWindows/UnitLoadout";
import { StatStrip } from "../../lib/encounter/combatWindows/unitStats";
import { UnitSheetVitals } from "../../lib/encounter/combatWindows/unitVitals";
import { pilotBuildView } from "../../lib/encounter/unitBuildView";
import type { Ref } from "../compendium/compendiumData";
import type { PreselectBonus } from "./DiceApp";
import { AvatarImage } from "./PilotAvatar";
import { usePilotSheetEdit, type PilotSheetEdit } from "./PilotSheetEdit";
import type { SectionFlag } from "../../lib/encounter/unitLoadout";
// `.be-textarea` (NOTES, the custom background) and `.be-stepper` (EDIT's steppers).
import "./brutalist-editor.css";

const MFR_NAME = new Map(listManufacturers().map((m) => [m.id, m.name]));

export function PilotBuildSheet({
  pilot,
  computed,
  playerName,
  onOpenInCompendium,
  onOpenMech,
  onCommitNotes,
  onRoll,
  showCarried,
  actions,
  edit,
}: {
  pilot: PilotData;
  computed: ComputedPilot | undefined;
  playerName?: string;
  onOpenInCompendium: (ref: Ref) => void;
  /** Open a mech's page from the ACTIVE MECH card; `undefined` prints the card without a link. */
  onOpenMech?: (mechId: string) => void;
  onCommitNotes: ((value: string) => void) | null;
  onRoll: ((preselect: PreselectBonus) => void) | null;
  showCarried: boolean;
  /** The host's header controls. Read-only hosts pass none. */
  actions?: ReactNode;
  /** The editing capability: see the module docstring. */
  edit: PilotSheetEdit | null;
}) {
  const { compendium } = useCompendiumContext();
  const fictionOnly = useFictionOnly();
  const view = pilotBuildView({ pilot, computed, compendium, wear: showCarried, fictionOnly });
  const build = view.build.map((g) => ({ ...g, empty: "— none allocated —" }));
  const editing = usePilotSheetEdit({ edit, pilot, computed, compendium, onOpenInCompendium });
  const groups = editing.groups ? editing.groups(view.loadout, build) : [...view.loadout, ...build];

  return (
    <UnitSheet
      posture="build"
      className="unit-sheet-build is-pilot"
      identity={{
        name: pilot.name || pilot.callsign || "Unnamed operator",
        kindLine: ["PILOT", pilot.callsign || "NO CALLSIGN", ...(playerName ? [playerName] : [])],
        level: pilot.level,
        templates: [],
        side: "pc",
        hidden: false,
      }}
      art={
        pilot.image ? (
          <AvatarImage src={pilot.image} alt="Pilot portrait" className="unit-sheet-art-portrait" fallback={<span className="unit-sheet-art-empty unit-sheet-art-portrait">PILOT IMG</span>} />
        ) : (
          <span className="unit-sheet-art-empty unit-sheet-art-portrait">PILOT IMG</span>
        )
      }
      badges={editing.headerControls}
      actions={
        actions || editing.toggle ? (
          <>
            {actions}
            {editing.toggle}
          </>
        ) : undefined
      }
      nameEditor={editing.nameEditor}
      toolbar={editing.toolbar}
      error={editing.error}
    >
      <div className="unit-sheet-cols">
        <div className="unit-sheet-col">
          {view.hp && <UnitSheetVitals hp={view.hp.value} maxHp={view.hp.max} exposed={false} shield={0} burn={0} onSetTrack={null} disabled />}
          {view.stats && <StatStrip cells={view.stats} onOpenDice={onRoll} />}
          {editing.level && <SheetSection title="LICENSE LEVEL">{editing.level}</SheetSection>}
          <SheetSection title="MECH SKILLS · HASE" flag={editing.hase?.flag} aside={editing.hase?.aside} anchor={editing.hase ? "hase" : undefined}>
            {editing.hase?.body ?? <StatStrip cells={view.hase} className="is-hase" onOpenDice={onRoll} />}
          </SheetSection>
          {editing.bondmate && <SheetSection title="BONDMATE">{editing.bondmate}</SheetSection>}
          <ActiveMech pilot={pilot} onOpenMech={onOpenMech} />
          <SheetNotes notes={pilot.notes} label="Pilot notes" placeholder="Anything you want to remember: plans, reminders, in-character thoughts." onCommit={onCommitNotes ?? editing.commitNotes ?? null} />
        </div>
        <div className="unit-sheet-col">
          <UnitLoadout
            groups={groups}
            onOpenRef={onOpenInCompendium}
            onOpenDice={onRoll ?? undefined}
            rowActions={editing.rowActions}
            groupFoot={editing.groupFoot}
            rowClass={editing.rowClass}
          />
          {editing.background ? <SheetSection title="BACKGROUND">{editing.background}</SheetSection> : <Background pilot={pilot} onOpenInCompendium={onOpenInCompendium} />}
        </div>
      </div>
      {editing.overlay}
    </UnitSheet>
  );
}

/**
 * A build-only section: the loadout group's ink-ruled head over its own body. `flag` paints EDIT's
 * outstanding work on the head; `anchor` is the FORGE anchor a jump lands on.
 */
export function SheetSection({ title, aside, flag, anchor, children }: { title: string; aside?: ReactNode; flag?: SectionFlag; anchor?: string; children: ReactNode }) {
  return (
    <section className="unit-sheet-section" aria-label={title.toLowerCase()} data-build-anchor={anchor}>
      <div className={`unit-sheet-lgroup-head${flag ? ` is-flag-${flag.severity}` : ""}`} title={flag?.title}>
        <span className="unit-sheet-lgroup-title">{title}</span>
        {aside}
      </div>
      <div className="unit-sheet-section-body">{children}</div>
    </section>
  );
}

/** NOTES: a textarea when the host grants `onCommit` (the owner's page), the notes as text otherwise. */
export function SheetNotes({ notes, label, placeholder, onCommit }: { notes: string | undefined; label: string; placeholder: string; onCommit: ((value: string) => void) | null }) {
  const draft = useDraftText(notes ?? "", onCommit ?? (() => {}));
  return (
    <SheetSection title="NOTES">
      {onCommit ? (
        <textarea
          className="be-textarea unit-sheet-notes"
          rows={5}
          maxLength={4000}
          value={draft.value}
          aria-label={label}
          placeholder={placeholder}
          onFocus={draft.onFocus}
          onChange={(e) => draft.onChange(e.target.value)}
          onBlur={draft.onBlur}
        />
      ) : notes ? (
        <UserText text={notes} className="unit-sheet-notes" />
      ) : (
        <div className="unit-sheet-section-empty">— no notes on file —</div>
      )}
    </SheetSection>
  );
}

function ActiveMech({ pilot, onOpenMech }: { pilot: PilotData; onOpenMech?: (mechId: string) => void }) {
  const { compendium } = useCompendiumContext();
  const mech = pilot.mechs.find((m) => m.id === resolveActiveMechId(pilot));
  if (!mech) {
    return (
      <SheetSection title="ACTIVE MECH">
        <div className="unit-sheet-section-empty">— no mech on file —</div>
      </SheetSection>
    );
  }
  const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
  const mfr = frame?.source ? (MFR_NAME.get(frame.source) ?? frame.source) : undefined;
  const chassis = frame ? `${frame.name.toUpperCase()}${mfr ? ` // ${mfr}` : ""}` : "NO CHASSIS";
  const art = resolveMechArtUrl(mech);
  const body = (
    <>
      <span className="unit-sheet-mechcard-art">{art && <AvatarImage src={art} alt="" fallback={null} />}</span>
      <span className="unit-sheet-mechcard-text">
        <b>{mech.name || "Unnamed mech"}</b>
        <span>{chassis}</span>
      </span>
      {onOpenMech && <span className="unit-sheet-mechcard-go">OPEN ›</span>}
    </>
  );
  return (
    <SheetSection title="ACTIVE MECH">
      {onOpenMech ? (
        <button type="button" className="unit-sheet-mechcard" onClick={() => onOpenMech(mech.id)}>
          {body}
        </button>
      ) : (
        <div className="unit-sheet-mechcard">{body}</div>
      )}
    </SheetSection>
  );
}

function Background({ pilot, onOpenInCompendium }: { pilot: PilotData; onOpenInCompendium: (ref: Ref) => void }) {
  const { compendium } = useCompendiumContext();
  const ref = pilot.backgroundRef;
  const selected = ref ? compendium.packs.find((p) => p.id === ref.packId)?.items.background.find((b) => b.id === ref.id) : undefined;
  const skills = (selected?.skills ?? []).map((id) => ({ id, name: compendium.getById("skill", id)?.name ?? id }));
  return (
    <SheetSection title={`BACKGROUND${pilot.background ? (ref ? " · REGISTERED ORIGIN" : " · CUSTOM ORIGIN") : ""}`}>
      {!pilot.background ? (
        <div className="unit-sheet-section-empty">— no background on file —</div>
      ) : (
        <>
          <div className="unit-sheet-bg-name">{pilot.background}</div>
          {selected?.description ? (
            <RulesText html={selected.description} className="unit-sheet-bg-prose" />
          ) : ref ? (
            <div className="unit-sheet-section-empty">SOURCE PACK NOT LOADED · NAME RETAINED</div>
          ) : null}
          {skills.length > 0 && (
            <div className="unit-sheet-bg-skills">
              <span className="unit-sheet-label">SUGGESTED TRIGGERS · NOT GRANTED</span>
              {skills.map((s) => (
                <button key={s.id} type="button" className="unit-sheet-lrow-ref" onClick={() => onOpenInCompendium({ type: "skill", id: s.id })}>
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </SheetSection>
  );
}
